import type { PropsWithChildren, ReactElement, ReactNode } from "react";

import clsx from "clsx";

import { type PropsTelemetryCustom } from "@Components/Modules/Helpers";
import Text from "@Components/Text";
import TelemetryHoc from "@Components/telemetry-hoc/telemetry-hoc";
import * as stylex from "@stylexjs/stylex";

import {
    type PropsWithStyleX,
    type QueryMdMax,
    type QuerySmMax,
    type QueryXsMax,
    oldSizes,
} from "../../../styles/tokens.stylex";

import * as portalCss from "../Portal.module.css";

export interface IPanelSection extends PropsWithChildren, PropsWithStyleX, PropsTelemetryCustom {
    id?: string;
    title?: string;
    subTitle?: string;
    description?: string;
    redirectButton?: ReactElement;
    ajaxTrackAction?: string;
    initialInView?: boolean;
    compAfterButton?: ReactNode;
    hasLeftHeader?: boolean;
    className?: string;
    key?: number | string;
    wrapperStyle?: stylex.StyleXStyles;
    titleStyle?: stylex.StyleXStyles;
    subTitleStyle?: stylex.StyleXStyles;
    descriptionStyle?: stylex.StyleXStyles;
}

export default function PanelSection(props: IPanelSection) {
    const stylesClass = stylex.props(styles.panelSectionComponent, props.styleX);

    return (
        <section
            id={props.id}
            className={clsx(stylesClass.className, props.className)}
            style={stylesClass.style}
        >
            <div className={portalCss.standardContainerV2}>
                <TelemetryHoc telemetryCustom={props.telemetryCustom} styleX={styles.sectionColOne}>
                    {props.title && (
                        <Text
                            textElement="h2"
                            fontStyle="h1"
                            dangerouslySetInnerHTML={{ __html: props.title }}
                            styleX={[
                                styles.titleSection,
                                props.hasLeftHeader && styles.leftHeader,
                                props.titleStyle,
                            ]}
                        />
                    )}

                    <div>
                        {(props.subTitle || props.description) && (
                            <div {...stylex.props(styles.subTitleWrapper)}>
                                {props.subTitle && (
                                    <Text
                                        textElement="p"
                                        fontStyle="h4"
                                        dangerouslySetInnerHTML={{ __html: props.subTitle }}
                                        styleX={props.subTitleStyle}
                                    />
                                )}
                                {props.description && (
                                    <Text
                                        textElement="p"
                                        fontStyle="primaryText"
                                        dangerouslySetInnerHTML={{ __html: props.description }}
                                        styleX={props.descriptionStyle}
                                    />
                                )}
                            </div>
                        )}
                        <div {...stylex.props(styles.componentWrapper, props.wrapperStyle)}>
                            {props.children}
                        </div>
                    </div>
                    <div {...stylex.props(styles.redirectBtn)}>{props.redirectButton}</div>
                    {props.compAfterButton && (
                        <div {...stylex.props(styles.afterButton)}>{props.compAfterButton}</div>
                    )}
                </TelemetryHoc>
            </div>
        </section>
    );
}

const xsMax: QueryXsMax = "@media (max-width: 759px)";
const smMax: QuerySmMax = "@media (max-width: 989px)";
const mdMax: QueryMdMax = "@media (max-width: 1239px)";

const styles = stylex.create({
    afterButton: {
        marginTop: { default: "60px", [xsMax]: "50px" },
    },

    redirectBtn: {
        display: "flex",
        justifyContent: "center",
        marginBottom: 5,
        marginTop: 40,
    },

    subTitleWrapper: {
        padding: { default: "0 10%", [xsMax]: "0 3%" },
        textAlign: "center",
        marginBottom: "40px",
        width: "100%",
    },

    componentWrapper: {
        width: "100%",
    },

    titleSection: {
        textAlign: "center",
        marginBottom: "40px",
    },
    leftHeader: {
        fontSize: { default: "1.875rem", [xsMax]: "1.5rem" },
        fontWeight: 700,
        textAlign: "left",
        marginBottom: "15px",
    },

    panelSectionComponent: {
        overflow: "hidden",
        marginBottom: { default: "100px", [smMax]: "80px", [xsMax]: "60px" },
        width: "100%",
    },

    sectionColOne: {
        padding: {
            default: `0px ${oldSizes.col1} `,
            [mdMax]: 0,
        },
    },
});
